








import { Component, Vue } from 'vue-property-decorator'
import { DoughnutChart } from 'vue-chart-3'
import {
    ActiveElement,
    ArcElement,
    Chart,
    ChartData,
    ChartEvent,
    ChartOptions,
    DoughnutController,
    LegendElement,
    LegendItem,
} from 'chart.js'
import ChartDataLabels from 'chartjs-plugin-datalabels'
import { namespace } from 'vuex-class'
import { Pipeline } from '@/api/model/pipeline'
import { PipelinePhaseCategory } from '@/api/types'
import { ReferralStats } from '@/api/model/referral-stats'
import { getColorForPhaseCategory } from '@/helpers/color'

const referrals = namespace('referrals')
Chart.register(DoughnutController, ArcElement)

@Component({
    components: {
        DoughnutChart,
    },
})
export default class ChartReferralPhaseCategory extends Vue {
    @referrals.Getter('selectablePipelines') readonly pipelines!: Pipeline[]
    @referrals.State readonly referralStats!: ReferralStats
    @referrals.State readonly phaseCategoryFilter!: PipelinePhaseCategory
    @referrals.Action applyPipelinePhaseCategoryFilter: ({ category: PipelinePhaseCategory }) => Promise<void>

    get availableCategories(): PipelinePhaseCategory[] {
        const categories = [
            PipelinePhaseCategory.ENTRY,
            PipelinePhaseCategory.APPROACH,
            PipelinePhaseCategory.DECISION,
        ]

        return categories.filter(category => {
            return this.pipelines
                .flatMap(pipeline => pipeline.phases.map(phase => phase.category))
                .indexOf(category) >= 0
        })
    }

    get chartData(): ChartData {
        if (!this.chartValues) {
            return null
        }

        return {
            labels: this.chartLabels,
            datasets: [
                {
                    data: Object.values(this.chartValues),
                    backgroundColor: this.chartColors,
                    datalabels: {
                        color: 'rgba(255, 255, 255, .75)',
                    },
                },
            ],
        }
    }

    get chartOptions(): ChartOptions<'doughnut'> {
        return {
            responsive: true,
            maintainAspectRatio: true,
            aspectRatio: 3,
            animation: {
                easing: 'easeOutExpo',
            },
            plugins: {
                legend: {
                    position: 'right',
                    onClick: (e: ChartEvent, legendItem: LegendItem, legend: LegendElement) => {
                        // @ts-ignore type defines datasetIndex but it is index at runtime
                        this.onClick(legendItem.index)
                    },
                },
                tooltip: {
                    enabled: false,
                },
            },
            onClick: (event: ChartEvent, elements: ActiveElement[], chart: Chart) => {
                if (elements.length === 0) {
                    return
                }

                this.onClick(elements[0].index)
            },
        }
    }

    get chartPlugins(): object[] {
        return [
            ChartDataLabels,
        ]
    }

    get chartValues(): {[key: string]: number} {
        if (!this.referralStats) {
            return {}
        }

        const chartValues = {}
        this.availableCategories.forEach(category => {
            const entry = this.referralStats.countsPerCategory.find(v => v.category === category)
            chartValues[category] = entry?.count ?? 0
        })

        return chartValues
    }

    get chartLabels(): string[] {
        return this.availableCategories.map(category => {
            const label = this.$t(`pipeline-phase.category.${category}`).toString()
            const count = this.chartValues[category]

            return `${label} (${count})`
        })
    }

    get chartColors(): string[] {
        return this.availableCategories.map(category => {
            return getColorForPhaseCategory(category)
        })
    }

    onClick(selectedIndex: number): void {
        const category = Object.keys(this.chartValues)[selectedIndex]

        if (category) {
            this.applyPipelinePhaseCategoryFilter({
                category,
            })
        }
    }
}
